// B.3 typecheck-zero: File-local types + Props interface. See docs/plans/archive/2026-04-12-typecheck-zero.
import React, { useState, useEffect, useMemo, useCallback, useRef, type ComponentType, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import FormFieldsSectionRaw from '@/components/ui/drawer-sections/FormFieldsSection';
import ChildrenSectionRaw from '@/components/ui/drawer-sections/ChildrenSection';
import AttachmentsSectionRaw from '@/components/ui/drawer-sections/AttachmentsSection';
import ExpandableCardRaw from '@/components/ui/ExpandableCard';
import FieldsetCardRaw from '@/components/ui/FieldsetCard';
import GenericHeaderRaw from '@/components/ui/GenericHeader';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import { BulkEditForm as BulkEditFormRaw, useBulkEdit } from '@/components/ui/BulkEditField';
import FicharioTabsRaw from '@/components/ui/FicharioTabs';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatMoney, formatDate } from '@/utils/formatters';
import { supabase } from '@/lib/supabase';
import { isSessionReconnecting } from '@/lib/refreshHealth';
import { useReloadOnReconnect } from '@/hooks/useRefreshHealth';
import { isReconnectingError, isReconnectingText } from '@/services/errors';
import { useRefreshHealth } from '@/hooks/useRefreshHealth';
import { checkChildrenBeforeDelete, CHILD_TABLES_CONFIG } from '@/utils/checkChildrenBeforeDelete';
import { getToastMessagesForTable } from '@/utils/toastMessagesConfig';
import { getErrorMessage } from '@/utils/errorMessageTranslator';
import { validateRequiredFields } from '@/utils/validateRequiredFields';
import { useSchemaMetadata } from '@/hooks/useSchemaMetadata';

// Single-step Raw-alias casts at import boundary: these sub-components are still untyped/partially-typed
// JS-style siblings. Cast to permissive ComponentType so GenericDrawer can forward richer prop shapes
// without modifying those files. Follow-up: type each sub-component in future Phase B sweeps.
const FormFieldsSection = FormFieldsSectionRaw as unknown as ComponentType<Record<string, unknown>>;
const ChildrenSection = ChildrenSectionRaw as unknown as ComponentType<Record<string, unknown>>;
const AttachmentsSection = AttachmentsSectionRaw as unknown as ComponentType<Record<string, unknown>>;
const ExpandableCard = ExpandableCardRaw as unknown as ComponentType<Record<string, unknown> & { children?: ReactNode }>;
const FieldsetCard = FieldsetCardRaw as unknown as ComponentType<Record<string, unknown> & { children?: ReactNode }>;
const GenericHeader = GenericHeaderRaw as unknown as ComponentType<Record<string, unknown> & { children?: ReactNode }>;
const BulkEditForm = BulkEditFormRaw as unknown as ComponentType<Record<string, unknown>>;
const FicharioTabs = FicharioTabsRaw as unknown as ComponentType<Record<string, unknown>>;

// === Type definitions (file-local) ===

export type GenericDrawerRow = Record<string, unknown>;

type DrawerMode = 'view' | 'edit' | 'create' | string;

interface StatusBadgeRenderArgs { isEditing: boolean; isCreating: boolean; onChange?: (field: string, value: unknown) => void }
export interface DrawerStatusBadge {
  condition?: boolean | ((data: GenericDrawerRow, config: GenericDrawerRow, ctx: StatusBadgeRenderArgs) => unknown);
  label?: ReactNode | ((data: GenericDrawerRow, config: GenericDrawerRow) => ReactNode);
  bgColor?: string | ((data: GenericDrawerRow, config: GenericDrawerRow) => string | undefined);
  textColor?: string | ((data: GenericDrawerRow, config: GenericDrawerRow) => string | undefined);
  borderColor?: string | ((data: GenericDrawerRow, config: GenericDrawerRow) => string | undefined);
  render?: (data: GenericDrawerRow, config: GenericDrawerRow, ctx: StatusBadgeRenderArgs) => ReactNode;
  [key: string]: unknown;
}

export interface DrawerField {
  field: string;
  label?: string | ((data: Record<string, unknown>) => string);
  // Mensagem customizada de campo obrigatório. Se omitido, usa `${label} é obrigatório`.
  // Útil para concordância de gênero ou contexto dinâmico (ex.: PF vs PJ).
  requiredMessage?: string | ((data: Record<string, unknown>) => string);
  type?: string;
  required?: boolean;
  readonly?: boolean;
  computed?: boolean;
  hidden?: boolean;
  lookupKey?: string;
  lookup?: string;
  options?: unknown;
  placeholder?: string;
  labelColor?: string;
  valueColor?: string;
  bold?: boolean;
  italic?: boolean;
  format?: (value: unknown, formData: GenericDrawerRow, config: GenericDrawerRow) => ReactNode;
  [key: string]: unknown;
}

export interface DrawerCard {
  id?: string;
  title?: ReactNode;
  showTitle?: boolean;
  rows?: unknown[];
  fields?: DrawerField[];
  [key: string]: unknown;
}

export interface DrawerSection {
  id?: string;
  component?: string | ComponentType<Record<string, unknown>>;
  title?: string;
  showAsTab?: boolean;
  tabIcon?: ReactNode;
  tabBadge?: ReactNode;
  position?: string;
  props?: Record<string, unknown>;
  config?: {
    fields?: DrawerField[];
    cards?: DrawerCard[];
    permissions?: DrawerPermissions;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface DrawerCustomSection {
  id: string;
  component: ComponentType<Record<string, unknown>>;
  showAsTab?: boolean;
  title?: string;
  tabIcon?: ReactNode;
  tabBadge?: ReactNode;
  position?: string;
  props?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DrawerExpandableCard {
  id: string;
  title?: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode | ((data: GenericDrawerRow) => ReactNode);
  badgeColor?: string;
  defaultExpanded?: boolean;
  showAsTab?: boolean;
  noPadding?: boolean;
  headerAction?: ReactNode | ((ctx: { mode: DrawerMode; isEditing: boolean; formData: GenericDrawerRow; config: GenericDrawerRow }) => ReactNode);
  component?: ComponentType<Record<string, unknown>>;
  render?: (ctx: { mode: DrawerMode; isEditing: boolean; formData: GenericDrawerRow; onChange: (field: string, value: unknown) => void; lookups: Record<string, unknown>; config: GenericDrawerRow }) => ReactNode;
  props?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DrawerSideBySideCard {
  id: string;
  title?: ReactNode;
  fields: Array<DrawerField & { field: string }>;
  [key: string]: unknown;
}

export interface DrawerPermissions {
  canEdit?: boolean;
  canDelete?: boolean;
  [key: string]: unknown;
}

export interface DrawerChildTableConfig {
  table: string;
  column?: string;
  label?: string;
  [key: string]: unknown;
}

interface SaveResult {
  deferred?: boolean;
  errors?: Record<string, string>;
  data?: GenericDrawerRow;
  id?: string | number;
  [key: string]: unknown;
}

interface DeleteResult {
  deferred?: boolean;
  [key: string]: unknown;
}

export interface DrawerBulkEditField {
  name: string;
  label?: string;
  type?: string;
  lookup?: string;
  options?: unknown;
  placeholder?: string;
  [key: string]: unknown;
}

export interface DrawerRenderArgs {
  mode: DrawerMode;
  isEditing: boolean;
  isCreating: boolean;
  formData: GenericDrawerRow;
  config: GenericDrawerRow;
  onChange?: (field: string, value: unknown) => void;
  lookups?: Record<string, unknown>;
  errors?: Record<string, string>;
  isFieldRequired?: (table: string, field: string) => boolean;
  tableName?: string;
  appliedFilters?: Record<string, unknown>;
  processedDefaultValues?: Record<string, unknown>;
  isBulkSelecting?: boolean;
  saving?: boolean;
  /** JOSH-89: the last save waits for the session to reconnect. */
  awaitingConnection?: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  statusBadges?: DrawerStatusBadge[];
  extraButtons?: ReactNode | null;
  subtitle?: ReactNode;
  title?: ReactNode;
  [key: string]: unknown;
}

export interface GenericDrawerProps {
  isOpen?: boolean;
  onClose?: (hadChanges?: boolean) => void;
  mode?: DrawerMode;
  onModeChange?: (mode: DrawerMode) => void;
  recordId?: string | number | null;
  isCreating?: boolean;
  record?: GenericDrawerRow | null;
  onSave?: (idOrFields?: unknown, isCreating?: boolean, updatedData?: GenericDrawerRow) => void;
  onDelete?: () => void;
  width?: string;
  mobileWidth?: string;
  title?: ReactNode | ((formData: GenericDrawerRow) => ReactNode);
  subtitle?: ReactNode;
  statusBadges?: DrawerStatusBadge[];
  sections?: DrawerSection[];
  customSections?: DrawerCustomSection[];
  lookups?: Record<string, unknown>;
  permissions?: DrawerPermissions | null;
  config?: GenericDrawerRow & { titulo?: ReactNode };
  defaultValues?: Record<string, unknown>;
  onLoadRecord?: (id: string | number) => Promise<GenericDrawerRow | null | undefined> | GenericDrawerRow | null | undefined;
  onSaveRecord?: (data: GenericDrawerRow, isCreating: boolean) => Promise<SaveResult | void> | SaveResult | void;
  onDeleteRecord?: (id: string | number) => Promise<DeleteResult | void> | DeleteResult | void;
  tableName?: string;
  recurso?: string;
  cards?: DrawerCard[];
  expandableCards?: DrawerExpandableCard[];
  sideBySideCards?: DrawerSideBySideCard[];
  renderHeader?: (args: DrawerRenderArgs) => ReactNode;
  renderFooter?: (args: DrawerRenderArgs) => ReactNode;
  renderContent?: (args: DrawerRenderArgs) => ReactNode;
  extraButtons?: ReactNode;
  zIndex?: number;
  /**
   * When true, this drawer ignores the Escape key (its document-level Escape
   * listener early-returns). Used by a parent drawer that mounts a nested
   * create-drawer on top: while the child is open, the parent must not
   * close/dirty itself on Escape — the child (registered later, or with its
   * own listener) handles Escape instead. Opt-in; defaults to false.
   */
  suppressEscapeClose?: boolean;
  loading?: boolean;
  isBulkSelecting?: boolean;
  bulkSelectIds?: Array<string | number>;
  bulkSelectFields?: DrawerBulkEditField[] | null;
  asyncSearchFunctions?: Record<string, unknown>;
  childTables?: DrawerChildTableConfig[];
  beforeClose?: () => boolean | void;
  appliedFilters?: Record<string, unknown>;
  refreshKey?: number;
  [key: string]: unknown;
}

export interface DrawerHeaderProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  isEditing?: boolean;
  isCreating?: boolean;
  saving?: boolean;
  /** JOSH-89: Salvar shows "Aguardando conexão" while the session reconnects. */
  awaitingConnection?: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  statusBadges?: DrawerStatusBadge[];
  formData?: GenericDrawerRow;
  config?: GenericDrawerRow;
  extraButtons?: ReactNode;
  onChange?: (field: string, value: unknown) => void;
}

/**
 * DESIGN SYSTEM - Padrões visuais exportados para uso em componentes filhos
 * Estes estilos definem o padrão visual para todo o sistema de drawers
 */
export const DRAWER_STYLES = {
  // === CORES ===
  colors: {
    primary: 'var(--primary)',        // Azul Joshua - botões principais
    primaryHover: 'var(--primary-700)',   // Hover do azul primário
    primaryLight: 'var(--color-primary-light)',   // Background azul claro
    primaryBorder: '#bfdbfe',  // Borda azul claro
    success: 'var(--success)',        // Verde - sucesso, quitado
    successDark: '#15803d',    // Verde escuro para texto
    successBg: 'var(--color-success-bg)',      // Background verde claro
    successBorder: '#bbf7d0',  // Borda verde claro
    danger: 'var(--color-danger)',         // Vermelho - erro, exclusão
    dangerHover: 'var(--danger)',    // Hover vermelho
    dangerDark: 'var(--color-danger-dark)',     // Vermelho escuro
    dangerBg: 'var(--color-danger-bg)',       // Background vermelho claro
    dangerBorder: '#fecaca',   // Borda vermelho claro
    warning: 'var(--color-warning)',        // Amarelo/laranja - alertas
    warningDark: '#b45309',    // Amarelo escuro para texto
    warningBg: '#fef3c7',      // Background amarelo claro
    warningBorder: '#fde68a',  // Borda amarelo claro
    text: '#1f2937',           // Texto principal
    textSecondary: '#374151',  // Texto secundário
    muted: 'var(--muted)',          // Texto cinza (var(--muted))
    mutedLight: '#9ca3af',     // Cinza mais claro
    border: 'var(--panel-border)', // Bordas padrão
    background: '#fafafa',     // Background de áreas
    backgroundWhite: 'white',  // Background branco
    backgroundHover: 'var(--color-bg-hover)', // Hover de elementos
    link: '#2563eb',           // Links
    linkDark: '#1d4ed8',       // Links hover
    linkBg: 'var(--color-primary-light)',         // Background de links
    linkBorder: '#bfdbfe',     // Borda de links
    editIcon: 'var(--color-info)',       // Cor do ícone de editar
    deleteIcon: 'var(--color-danger)'      // Cor do ícone de excluir
  },

  // === TIPOGRAFIA ===
  typography: {
    title: { fontSize: '16px', fontWeight: 600, color: '#1f2937' },
    subtitle: { fontSize: '13px', color: 'var(--muted)' },
    label: { fontSize: '12px', color: 'var(--muted)', marginBottom: '4px', display: 'block' },
    labelSmall: { fontSize: '11px', color: 'var(--muted)' },
    labelSection: { fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '12px' },
    value: { fontSize: '14px', color: '#374151' },
    valueBold: { fontSize: '14px', fontWeight: 700, color: '#374151' },
    valueLarge: { fontSize: '16px', fontWeight: 600, color: '#374151' },
    badge: { fontSize: '12px', fontWeight: 500 },
    button: { fontSize: '13px', fontWeight: 500 },
    buttonSmall: { fontSize: '12px' }
  },

  // === ESPAÇAMENTOS ===
  spacing: {
    drawerPadding: '20px',
    headerPadding: '16px 20px',
    cardPadding: '12px 16px',
    inputPadding: '10px 12px',
    buttonPadding: '8px 16px',
    buttonPaddingSmall: '4px 10px',
    badgePadding: '4px 10px',
    gapXs: '4px',
    gapSm: '8px',
    gapMd: '12px',
    gapLg: '16px',
    gapXl: '20px',
    cardMargin: '12px',          // Espaçamento padrão entre cards (igual V1)
    cardMarginCompact: '8px',    // Espaçamento compacto (uso especial)
    // IMPORTANTE: O container de conteúdo do drawer NÃO deve usar gap.
    // O espaçamento entre cards é controlado pelo marginBottom de cada card (cardMargin: 12px).
    // Isso garante espaçamento uniforme igual ao V1 original.
    contentContainerGap: 'none'  // NÃO usar gap no container de conteúdo
  },

  // === BORDAS ===
  borders: {
    radius: '6px',
    radiusSmall: '4px',
    radiusLarge: '8px',
    radiusRound: '12px',
    radiusFull: '50%',
    default: '1px solid var(--panel-border)'
  },

  // === SOMBRAS ===
  shadows: {
    card: '0 1px 3px rgba(0,0,0,0.08)',
    cardHover: '0 2px 6px rgba(0,0,0,0.12)',
    drawer: '-4px 0 20px rgba(0, 0, 0, 0.15)',
    modal: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    button: '0 1px 3px rgba(0,0,0,0.1)',
    buttonHover: '0 2px 6px rgba(0,0,0,0.15)',
    buttonPrimary: '0 2px 6px rgba(21, 70, 128, 0.4)'
  },

  // === TRANSIÇÕES ===
  transitions: {
    default: 'all 0.2s ease',
    fast: 'all 0.15s ease'
  }
};

/**
 * Estilos de componentes pré-definidos
 */
export const DRAWER_COMPONENTS = {
  // === DRAWER CONTAINER (estrutura principal) ===
  drawerOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },
  drawerContainer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    background: 'white',
    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  drawerHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--panel-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'white'
  },
  drawerContent: {
    flex: 1,
    overflow: 'auto',
    padding: '20px',
    background: 'white'
  },

  // === CONTENT CONTAINER (Padrão V1) ===
  // IMPORTANTE: NÃO usar gap no container de conteúdo.
  // O espaçamento é controlado pelo marginBottom de cada card.
  contentContainer: {
    display: 'flex',
    flexDirection: 'column'
    // NÃO adicionar gap aqui - usar marginBottom nos cards
  },

  // === CARDS ===
  card: {
    background: 'white',
    border: '1px solid var(--panel-border)',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '12px 16px',
    marginBottom: '12px'  // Espaçamento entre cards (padrão V1)
  },

  // === INPUTS ===
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--panel-border)',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
    outline: 'none'
  },

  // === BOTÕES ===
  // IMPORTANTE: Ambos os botões têm border de 1px para garantir mesma altura
  buttonPrimary: {
    background: 'linear-gradient(135deg, #2070b8 0%, #0f3460 100%)',
    color: 'white',
    border: '1px solid #0f3460',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '1',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(21, 70, 128, 0.3)'
  },
  buttonGhost: {
    backgroundColor: 'white',
    color: 'var(--muted)',
    border: '1px solid #e5e7eb',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '1',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  buttonIcon: {
    background: 'transparent',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px',
    transition: 'all 0.15s ease'
  },
  buttonDanger: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, var(--color-danger-gradient) 0%, var(--color-danger-gradient-dark) 100%)',
    color: 'white',
    border: '1px solid var(--color-danger-gradient-dark)',
    borderRadius: '6px',
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(139, 0, 0, 0.3)',
    transition: 'all 0.2s ease'
  },

  // === BADGES/STATUS (sem ícones - apenas texto) ===
  badge: {
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center'
  },
  badgeSuccess: {
    background: 'var(--color-success-bg)',
    color: '#15803d',
    border: '1px solid #bbf7d0'
  },
  badgeWarning: {
    background: '#fef3c7',
    color: '#b45309',
    border: '1px solid #fde68a'
  },
  badgeDanger: {
    background: 'var(--color-danger-bg)',
    color: 'var(--danger)',
    border: '1px solid #fecaca'
  },
  badgeInfo: {
    background: 'var(--color-primary-light)',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe'
  },

  // === EXPANDABLE CARD (Seções expansíveis) ===
  // Padrão: chevron à ESQUERDA, sem ícones decorativos, espaçamento padrão 12px
  expandableCard: {
    container: {
      background: 'white',
      border: '1px solid var(--panel-border)',
      borderRadius: '8px',           // Cantos ligeiramente arredondados
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      marginBottom: '12px',          // Espaçamento padrão entre cards (igual V1)
      overflow: 'hidden'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease'
    },
    badge: {
      background: 'var(--panel-border)',         // Cinza mais escuro
      borderRadius: '12px',
      padding: '2px 8px',
      fontSize: '12px',
      color: '#374151',
      fontWeight: 500
    },
    chevron: {
      color: 'var(--muted)',
      transition: 'transform 0.2s ease'
    }
  },

  // === MODAIS ===
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002
  },
  modalContainer: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  },
  modalIconContainer: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600
  },
  modalSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--muted)'
  },
  modalFooter: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  }
};

/**
 * DrawerHeader - Componente de header reutilizável para drawers
 * Usa GenericHeader com mode="drawer" internamente
 *
 * @param {Object} props
 * @param {string} props.title - Título do drawer
 * @param {string} props.subtitle - Subtítulo opcional
 * @param {boolean} props.isEditing - Se está em modo de edição (edit ou create)
 * @param {boolean} props.isCreating - Se está criando novo registro
 * @param {boolean} props.saving - Se está salvando
 * @param {Function} props.onClose - Callback para fechar
 * @param {Function} props.onCancel - Callback para cancelar edição
 * @param {Function} props.onSave - Callback para salvar
 * @param {Function} props.onEdit - Callback para entrar em modo edição
 * @param {Function} props.onDelete - Callback para excluir
 * @param {boolean} props.canEdit - Se pode editar
 * @param {boolean} props.canDelete - Se pode excluir
 * @param {Array} props.statusBadges - Array de badges de status
 * @param {Object} props.formData - Dados do formulário (para avaliar condições dos badges)
 * @param {Object} props.config - Configuração dinâmica
 * @param {React.ReactNode} props.extraButtons - Botões extras opcionais
 */
export function DrawerHeader({
  title,
  subtitle,
  isEditing = false,
  isCreating = false,
  saving = false,
  awaitingConnection = false,
  onClose,
  onCancel,
  onSave,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = false,
  statusBadges = [],
  formData = {},
  config = {},
  extraButtons,
  onChange
}: DrawerHeaderProps) {
  // Renderiza badges de status como componente React
  const renderBadges = () => {
    if (!statusBadges || statusBadges.length === 0) return null;

    const visibleBadges = statusBadges.filter(badge => {
      if (typeof badge.condition === 'function') {
        return badge.condition(formData, config, { isEditing, isCreating, onChange });
      }
      return badge.condition !== false;
    });

    if (visibleBadges.length === 0) return null;

    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {visibleBadges.map((badge, index) => {
          // Suportar componente customizado via render function
          if (badge.render && typeof badge.render === 'function') {
            return <React.Fragment key={index}>{badge.render(formData, config, { isEditing, isCreating, onChange })}</React.Fragment>;
          }

          // Suportar bgColor/textColor como funções ou valores estáticos
          const bgColor = typeof badge.bgColor === 'function'
            ? badge.bgColor(formData, config)
            : badge.bgColor;
          const textColor = typeof badge.textColor === 'function'
            ? badge.textColor(formData, config)
            : badge.textColor;
          const borderColor = typeof badge.borderColor === 'function'
            ? badge.borderColor(formData, config)
            : badge.borderColor;

          return (
            <span
              key={index}
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                background: bgColor || 'var(--color-bg-hover)',
                color: textColor || '#374151',
                border: borderColor ? `1px solid ${borderColor}` : 'none'
              }}
            >
              {typeof badge.label === 'function' ? badge.label(formData, config) : badge.label}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <GenericHeader
      mode="drawer"
      title={title || (isCreating ? 'Novo Registro' : '')}
      secondaryTitle={subtitle}
      badge={renderBadges()}
      isEditing={isEditing}
      saving={saving}
      awaitingConnection={awaitingConnection}
      onClose={onClose}
      onCancel={onCancel}
      onSave={onSave}
      onEdit={onEdit}
      onDelete={onDelete}
      canEdit={canEdit}
      canDelete={canDelete}
    >
      {extraButtons}
    </GenericHeader>
  );
}

/**
 * GenericDrawer - Componente genérico de drawer com layout idêntico ao LancamentoDrawer
 *
 * Suporta:
 * - Largura responsiva (50% desktop, 100% mobile)
 * - Status badges no header
 * - Cards expansíveis
 * - Layout de cards lado a lado (composição/situação)
 * - Seções genéricas: form, children (tabelas filho), attachments
 * - Componentes plugáveis via customSections
 * - Modos: view, edit, create
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Se o drawer está aberto
 * @param {Function} props.onClose - Callback para fechar
 * @param {'view' | 'edit' | 'create'} props.mode - Modo atual
 * @param {Function} props.onModeChange - Callback para mudar modo
 * @param {string} props.recordId - ID do registro (null se criando)
 * @param {boolean} props.isCreating - Se está criando novo registro
 * @param {Object} props.record - Dados do registro
 * @param {Function} props.onSave - Callback após salvar
 * @param {Function} props.onDelete - Callback após excluir
 * @param {string} props.width - Largura do drawer (default: '50%')
 * @param {string} props.mobileWidth - Largura em mobile (default: '100%')
 * @param {string} props.title - Título do drawer
 * @param {string} props.subtitle - Subtítulo
 * @param {Array} props.statusBadges - Badges de status no header
 * @param {Array} props.sections - Configuração das seções
 * @param {Array} props.customSections - Componentes plugáveis
 * @param {Object} props.lookups - Dados auxiliares para lookups
 * @param {Object} props.permissions - Permissões
 * @param {Object} props.config - Configuração dinâmica (do configBuilder)
 * @param {Array} props.cards - Configuração de cards (para FormFieldsSection)
 *   - card.title: Título do card (opcional)
 *   - card.showTitle: Controle explícito de exibição do título
 *     - true: sempre mostra título (mesmo com 1 card)
 *     - false: nunca mostra título
 *     - undefined (default): mostra título apenas se houver mais de 1 card
 * @param {Array} props.expandableCards - Cards expansíveis (movimentos, anexos)
 * @param {Object} props.sideBySideCards - Cards lado a lado (composição/situação)
 */
export default function GenericDrawer({
  isOpen,
  onClose,
  mode = 'view',
  onModeChange,
  recordId,
  isCreating = false,
  record,
  onSave,
  onDelete,
  width = '50%',
  mobileWidth = '100%',
  title,
  subtitle,
  statusBadges = [],
  sections = [],
  customSections = [],
  lookups = {},
  permissions = {},
  config = {},
  defaultValues = {},
  onLoadRecord,
  onSaveRecord,
  onDeleteRecord,
  tableName,
  recurso,
  cards = [],
  expandableCards = [],
  sideBySideCards,
  renderHeader,
  renderFooter,
  renderContent,
  extraButtons, // Botões extras no header (antes do botão editar)
  zIndex = 1000, // z-index do overlay (drawer será zIndex + 1)
  suppressEscapeClose = false, // Ignora Escape enquanto um create-drawer aninhado está aberto
  loading: externalLoading = false, // loading externo passado pelo parent
  // === Props de Bulk Edit ===
  isBulkSelecting = false,
  bulkSelectIds = [],
  bulkSelectFields = null, // null = usa todos os campos do form
  asyncSearchFunctions = {}, // Funções de busca async para campos async-select no bulk edit
  // === Props de verificação de filhos antes de deletar ===
  childTables, // Array de { table, column, label } - se não informado, usa CHILD_TABLES_CONFIG[tableName]
  // === Props de interceptação de fechamento ===
  beforeClose, // Função chamada antes de animar o fechamento. Se retornar false, impede o fechamento.
  // === Props de filtros aplicados (do GenericList) ===
  appliedFilters = {}, // Filtros avançados aplicados na lista (ex: { obra_id: ['uuid1'] })
  // === Props de Realtime/Refresh Externo ===
  refreshKey = 0, // Quando muda, força reload dos dados (útil para Realtime)
  ...otherProps
}: GenericDrawerProps) {
  // === Multi-tenant: obter campos de auditoria ===
  const { getAuditFields } = useAuth();
  const { showSuccess, showError } = useToast();

  // === Schema-driven UI: campos required derivados do banco ===
  const { enrichFieldsWithRequired, isFieldRequired } = useSchemaMetadata();

  // === Toast messages automáticas baseadas no tableName ===
  const toastMessages = useMemo(() => getToastMessagesForTable(tableName), [tableName]);

  // === Resolver childTables automaticamente se não informado ===
  // Se childTables não foi passado, tenta usar a configuração centralizada baseada no tableName
  const effectiveChildTables = useMemo<DrawerChildTableConfig[]>(() => {
    if (childTables !== undefined) return childTables;
    if (tableName) {
      // CHILD_TABLES_CONFIG is a typed literal map; narrow tableName to its keys.
      const cfg = (CHILD_TABLES_CONFIG as Record<string, DrawerChildTableConfig[]>)[tableName];
      if (cfg) return cfg;
    }
    return [];
  }, [childTables, tableName]);

  // === Estados ===
  const [formData, setFormData] = useState<GenericDrawerRow>({});
  const [originalData, setOriginalData] = useState<GenericDrawerRow>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // JOSH-89: the last save was answered SESSION_RECONNECTING; Salvar shows
  // "Aguardando conexão" until health is ok again.
  const [saveAwaitingConnection, setSaveAwaitingConnection] = useState<boolean>(false);
  const sessionReconnecting = useRefreshHealth().status === 'reconnecting';
  const awaitingConnection = saveAwaitingConnection && sessionReconnecting;
  const [activeTab, setActiveTab] = useState<string>('dados');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showDiscardModal, setShowDiscardModal] = useState<boolean>(false);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);

  // === Ref para controlar animação de abertura (evitar repetição) ===
  const hasOpenedRef = useRef<boolean>(false);

  // === Ref para evitar recarregar registro quando dependências externas mudam ===
  const loadedRecordIdRef = useRef<string | number | null>(null);

  // === Ref para evitar reinicializar formData quando isCreating e deps mudam ===
  // Problema: se o drawer pai re-renderiza, processedDefaultValues muda de referência,
  // o que dispara o useEffect e RESETA o formData, perdendo a digitação do usuário.
  const creatingInitializedRef = useRef<boolean>(false);

  // === Cache de componentes customSection para evitar remount ===
  // Problema: se o drawer pai define componentes DENTRO da função (ex: const MySection = () => {...}),
  // a cada render do pai uma NOVA função é criada. React vê como componente diferente e faz remount.
  // Solução: cachear a primeira referência de cada section.id e reutilizar nas próximas renderizações.
  const customSectionComponentsRef = useRef<Record<string, ComponentType<Record<string, unknown>>>>({});

  // Helper: obter componente cacheado (ou cachear na primeira vez)
  const getCachedComponent = useCallback((sectionId: string, component: ComponentType<Record<string, unknown>>): ComponentType<Record<string, unknown>> => {
    if (!customSectionComponentsRef.current[sectionId]) {
      customSectionComponentsRef.current[sectionId] = component;
    }
    return customSectionComponentsRef.current[sectionId];
  }, []);

  // === Bulk Edit Hook ===
  const {
    selectedFields: selectedBulkFields,
    toggleField: toggleBulkField,
    resetFields: resetBulkFields,
    getSelectedData: getSelectedBulkData,
    hasSelectedFields: hasBulkFieldsSelected
  } = useBulkEdit();

  // === Detectar mobile ===
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // === Reset bulk edit fields quando drawer abre/fecha ou muda modo ===
  useEffect(() => {
    if (!isOpen || !isBulkSelecting) {
      resetBulkFields();
    }
  }, [isOpen, isBulkSelecting, resetBulkFields]);

  // === Resetar estados quando drawer fecha ===
  useEffect(() => {
    if (!isOpen) {
      // Limpar estados quando drawer fecha para evitar mostrar dados antigos ao reabrir
      setLoading(false);
      setFormData({});
      setOriginalData({});
      setErrors({});
      setActiveTab('dados');
      loadedRecordIdRef.current = null; // Resetar ref para permitir reload ao reabrir
    }
  }, [isOpen]);

  // === Setar loading imediatamente quando recordId muda ===
  // Isso garante que o drawer mostre "Carregando..." enquanto busca novos dados
  // IMPORTANTE: Só setar loading se o registro realmente vai ser carregado (ID diferente)
  // Caso contrário, onLoadRecord mudando de referência causaria loading permanente
  useEffect(() => {
    if (isOpen && recordId && onLoadRecord) {
      if (loadedRecordIdRef.current !== recordId) {
        setLoading(true);
      }
    }
  }, [isOpen, recordId, onLoadRecord]);

  // === Processar appliedFilters para pré-seleção automática ===
  // Para cada campo em appliedFilters que tem exatamente 1 valor, usar como defaultValue
  const processedDefaultValues = useMemo<Record<string, unknown>>(() => {
    const result: Record<string, unknown> = { ...defaultValues };

    if (appliedFilters && typeof appliedFilters === 'object') {
      Object.entries(appliedFilters).forEach(([key, values]) => {
        // Se o filtro tem exatamente 1 valor E o campo não tem defaultValue definido
        if (Array.isArray(values) && values.length === 1 && !result[key]) {
          result[key] = values[0];
        }
      });
    }

    return result;
  }, [defaultValues, appliedFilters]);

  // JOSH-89: set when a record load was answered SESSION_RECONNECTING.
  const reloadOnReconnectRef = useRef<boolean>(false);

  // === Função de carregamento (useCallback antes do useEffect) ===
  const loadRecord = useCallback(async () => {
    if (!onLoadRecord || !recordId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await onLoadRecord(recordId);
      setFormData((data || {}) as GenericDrawerRow);
      setOriginalData((data || {}) as GenericDrawerRow);
    } catch (error) {
      if (isReconnectingError(error)) {
        // JOSH-89: keep what is shown; load again when the session is back.
        reloadOnReconnectRef.current = true;
      } else {
        console.error('[GenericDrawer] Erro ao carregar registro:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [onLoadRecord, recordId]);

  // JOSH-89: a record load answered SESSION_RECONNECTING runs once more on
  // health ok. A failed save needs nothing here: the form stays open and its
  // inline `errors.submit` carries the reconnecting message.
  useReloadOnReconnect(() => {
    if (!reloadOnReconnectRef.current) return;
    reloadOnReconnectRef.current = false;
    void loadRecord();
  });

  // JOSH-89: on health ok the save button comes back and the inline
  // reconnecting message is dropped; the form keeps its input.
  useEffect(() => {
    if (sessionReconnecting || !saveAwaitingConnection) return;
    setSaveAwaitingConnection(false);
    setErrors((prev) => {
      if (!isReconnectingText(prev.submit)) return prev;
      const next = { ...prev };
      delete next.submit;
      return next;
    });
  }, [sessionReconnecting, saveAwaitingConnection]);

  // === Ref para rastrear último refreshKey processado ===
  const lastRefreshKeyRef = useRef<number>(refreshKey);

  // === Carregar registro ===
  useEffect(() => {
    if (isOpen && recordId && onLoadRecord) {
      // Carregar se:
      // 1. É um registro diferente do já carregado, OU
      // 2. refreshKey mudou (força reload via Realtime)
      const isNewRecord = loadedRecordIdRef.current !== recordId;
      const isRefreshRequested = refreshKey > 0 && lastRefreshKeyRef.current !== refreshKey;

      if (isNewRecord || isRefreshRequested) {
        loadedRecordIdRef.current = recordId;
        lastRefreshKeyRef.current = refreshKey;
        creatingInitializedRef.current = false; // Reset para próximo create
        if (isRefreshRequested) {
        }
        loadRecord();
      }
    } else if (isOpen && isCreating) {
      // Novo registro: inicializar com processedDefaultValues (inclui appliedFilters)
      // IMPORTANTE: Só inicializar UMA VEZ para não perder digitação do usuário
      if (!creatingInitializedRef.current) {
        creatingInitializedRef.current = true;
        loadedRecordIdRef.current = null;
        const initialValues = { ...processedDefaultValues };
        setFormData(initialValues);
        // P2-001 fix: snapshot the pre-filled defaults as the dirty baseline.
        // Previously originalData was {}, so hasUnsavedChanges() saw the
        // pre-filled date as "user-typed" and Cancel always prompted.
        setOriginalData(initialValues);
      }
    }
  }, [isOpen, recordId, isCreating, onLoadRecord, processedDefaultValues, loadRecord, refreshKey]);

  // Reset do flag quando drawer fecha
  useEffect(() => {
    if (!isOpen) {
      creatingInitializedRef.current = false;
      hasOpenedRef.current = false; // Reset animação para próxima abertura
    }
  }, [isOpen]);

  // Marcar que drawer já abriu (após primeiro render) para não repetir animação
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Usar setTimeout para permitir que a animação inicial ocorra
      const timer = setTimeout(() => {
        hasOpenedRef.current = true;
      }, 350); // Um pouco maior que a duração da animação (0.3s = 300ms)
      return () => clearTimeout(timer);
    }
  }, [isOpen, isClosing]);

  // Atualizar formData quando record muda externamente
  // IMPORTANTE: Não sobrescrever quando isCreating=true (modo add)
  useEffect(() => {
    if (record && !isCreating) {
      setFormData(record);
      setOriginalData(record);
    }
  }, [record, isCreating]);

  // Inicializar expandedCards
  useEffect(() => {
    if (expandableCards.length > 0) {
      const initial: Record<string, boolean> = {};
      expandableCards.forEach(card => {
        initial[card.id] = card.defaultExpanded !== false;
      });
      setExpandedCards(initial);
    }
  }, [expandableCards]);

  // === Determinar modo efetivo ===
  const isEditing = mode === 'edit' || isCreating;

  // === Handlers ===
  const handleFieldChange = (field: string, value: unknown): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const hasUnsavedChanges = (): boolean => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSave = async (): Promise<void> => {
    // === Modo Bulk Edit: retornar apenas campos selecionados ===
    if (isBulkSelecting) {
      if (!hasBulkFieldsSelected()) {
        setErrors({ submit: 'Selecione pelo menos um campo para editar' });
        return;
      }

      // Pegar apenas os campos marcados
      const fieldsToUpdate = getSelectedBulkData(formData) as Record<string, unknown>;

      // Processar campos async-select: extrair apenas o .value do objeto {value, label}
      // Isso é necessário porque AsyncSearchableSelect retorna objeto completo
      const processedFields: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(fieldsToUpdate)) {
        if (val && typeof val === 'object' && 'value' in val && 'label' in (val as object)) {
          // É um objeto de async-select, extrair só o value
          processedFields[key] = (val as { value: unknown }).value;
        } else {
          processedFields[key] = val;
        }
      }


      // Chamar onSave com os campos selecionados (GenericList vai tratar isso)
      if (onSave) {
        onSave(processedFields);
      }
      return;
    }

    // === Modo normal ===
    // Validação automática de campos required (Schema-driven UI: banco define NOT NULL, frontend valida)
    const allFields: DrawerField[] = formSection?.config?.fields || [];
    if (allFields.length > 0) {
      // Enriquecer campos com required do schema do banco (se tableName disponível)
      const enrichedFields = tableName
        ? enrichFieldsWithRequired(tableName, allFields)
        : allFields;

      const validation = validateRequiredFields(formData, enrichedFields, {
        onError: (msg: string) => showError({ title: 'Campo obrigatório', description: msg })
      });
      if (!validation.isValid) {
        setErrors(validation.errors as Record<string, string>);
        return;
      }
    }

    if (!onSaveRecord) return;

    setSaving(true);
    try {
      // Multi-tenant: adicionar campos de auditoria automaticamente
      const auditFields = getAuditFields() as unknown as Record<string, unknown>;
      const dataWithAudit: GenericDrawerRow = { ...formData };

      if (isCreating) {
        // INSERT: adicionar tenant_id, filial_id, created_by, updated_by
        if (auditFields.tenant_id && !dataWithAudit.tenant_id) {
          dataWithAudit.tenant_id = auditFields.tenant_id;
        }
        if (auditFields.filial_id && !dataWithAudit.filial_id) {
          dataWithAudit.filial_id = auditFields.filial_id;
        }
        if (auditFields.created_by && !dataWithAudit.created_by) {
          dataWithAudit.created_by = auditFields.created_by;
        }
        if (auditFields.updated_by && !dataWithAudit.updated_by) {
          dataWithAudit.updated_by = auditFields.updated_by;
        }
      } else {
        // UPDATE: adicionar apenas updated_by
        if (auditFields.updated_by) {
          dataWithAudit.updated_by = auditFields.updated_by;
        }
      }

      const result = (await onSaveRecord(dataWithAudit, isCreating)) as SaveResult | void | undefined;

      // Caller wants to handle completion itself (e.g. recurrence modal)
      if (result?.deferred) return;

      if (result?.errors) {
        setErrors(result.errors);
        // Toast de erro para validação customizada (campos obrigatórios do drawer)
        const [fieldName, errorMsg] = Object.entries(result.errors)[0] || [];
        if (fieldName && errorMsg) {
          // Se a mensagem já inclui o nome do campo, usar direto; senão, formatar
          const description = errorMsg.toLowerCase() === 'obrigatório'
            ? `${fieldName} é obrigatório`
            : errorMsg;
          showError({ title: 'Campo obrigatório', description });
        }
        return;
      }

      // Obter dados atualizados (do servidor ou do form)
      // Isso garante que dados calculados no servidor (ex: data com now()) sejam usados
      const updatedData: GenericDrawerRow = result?.data || formData;

      if (onSave) {
        // Passar updatedRecord como 3º argumento para evitar reload desnecessário da lista
        // O GenericList pode atualizar apenas o registro específico em vez de recarregar tudo
        onSave(result?.id || recordId, isCreating, updatedData);
      }

      // Toast de sucesso automático (se tableName estiver configurado)
      if (tableName) {
        const msg = (isCreating ? toastMessages.create : toastMessages.update) as { title: string; description: string };
        showSuccess({ title: msg.title, description: msg.description });
      }

      if (isCreating) {
        animateClose(true); // hadChanges = true (registro criado)
      } else {
        // Atualizar formData e originalData com dados retornados do save
        setFormData(updatedData);
        setOriginalData(updatedData);
        if (onModeChange) onModeChange('view');
      }
    } catch (error) {
      console.error('[GenericDrawer] Erro ao salvar:', error);
      // Traduzir erro técnico para mensagem amigável
      const friendlyError = getErrorMessage(error);
      setErrors({ submit: friendlyError.description });
      if (isReconnectingError(error) && isSessionReconnecting()) setSaveAwaitingConnection(true);
      // Toast de erro automático com mensagem traduzida
      showError({ title: friendlyError.title, description: friendlyError.description });
    } finally {
      setSaving(false);
    }
  };

  // Verifica filhos ANTES de mostrar o modal de confirmação
  const handleDeleteClick = async (): Promise<void> => {
    if (!recordId) return;

    // Se tem childTables configurado (explícito ou via tableName), verificar filhos primeiro
    if (effectiveChildTables && effectiveChildTables.length > 0) {
      // Single-step cast: DrawerChildTableConfig is a superset (optional column/label) of upstream ChildTableConfig.
      // Runtime shapes match; follow-up is to export the upstream type and reuse it here.
      const result = await checkChildrenBeforeDelete(
        supabase,
        String(recordId),
        effectiveChildTables as unknown as Parameters<typeof checkChildrenBeforeDelete>[2]
      );
      if (!result.canDelete) {
        // Toast estruturado com título e descrição separados
        showError({
          title: 'Exclusão bloqueada',
          description: result.message
        });
        return; // Não mostra o modal de confirmação
      }
    }

    // Se passou na verificação (ou não tem childTables), mostra o modal
    setShowDeleteConfirm(true);
  };

  const handleDelete = async (): Promise<void> => {
    if (!onDeleteRecord || !recordId) return;

    try {
      const result = (await onDeleteRecord(recordId)) as DeleteResult | void | undefined;

      // Caller wants to handle completion itself (e.g. recurrence modal)
      if (result?.deferred) {
        setShowDeleteConfirm(false);
        return;
      }

      setShowDeleteConfirm(false);

      // Toast de sucesso automático (se tableName estiver configurado)
      if (tableName) {
        const msg = toastMessages.delete as { title: string; description: string };
        showSuccess({ title: msg.title, description: msg.description });
      }

      if (onDelete) {
        onDelete();
      }
      animateClose(true); // hadChanges = true (registro excluído)
    } catch (error) {
      console.error('[GenericDrawer] Erro ao excluir:', error);
      setShowDeleteConfirm(false);
      // Traduzir erro técnico para mensagem amigável
      const friendlyError = getErrorMessage(error);
      showError({ title: friendlyError.title, description: friendlyError.description });
    }
  };

  const handleAttemptClose = () => {
    if (isEditing && hasUnsavedChanges()) {
      setShowDiscardModal(true);
    } else {
      handleConfirmDiscard();
    }
  };

  // P3-006/P3-021: Escape closes the drawer (a11y).
  // Routed through handleAttemptClose so dirty editing still triggers
  // the discard-confirmation modal — Escape never silently drops edits.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // A nested create-drawer is open on top — let it own Escape; this
        // (parent) drawer must not close/dirty itself. See suppressEscapeClose.
        if (suppressEscapeClose) return;
        if (showDiscardModal || showDeleteConfirm) {
          // Dismiss the inner confirmation modal first.
          setShowDiscardModal(false);
          setShowDeleteConfirm(false);
          return;
        }
        handleAttemptClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // handleAttemptClose is stable enough — closure reads latest state via refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showDiscardModal, showDeleteConfirm, isEditing, suppressEscapeClose]);

  const handleConfirmDiscard = () => {
    setShowDiscardModal(false);
    if (isCreating || isBulkSelecting) {
      // Em modo criação ou bulk edit, fechar o drawer com animação
      animateClose();
    } else {
      // Restaurar dados originais
      setFormData(originalData);
      setErrors({});
      if (onModeChange) onModeChange('view');
    }
  };

  const animateClose = (hadChanges: boolean = false): void => {
    setIsClosing(true);
    // Aguardar animação antes de fechar (300ms = duração da animação)
    setTimeout(() => {
      onClose?.(hadChanges);
      setIsClosing(false);
    }, 300);
  };

  const handleCancel = () => {
    handleAttemptClose();
  };

  const handleEdit = () => {
    if (onModeChange) onModeChange('edit');
  };

  const handleBackdropClick = () => {
    if (isEditing) {
      handleAttemptClose();
    } else {
      // beforeClose retornando false impede o fechamento (guard de descarte)
      if (beforeClose && beforeClose() === false) return;
      animateClose();
    }
  };

  const toggleExpandedCard = (cardId: string): void => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  /**
   * Processa cards aplicando lógica de exibição de títulos
   * - showTitle: true → sempre mostra
   * - showTitle: false → nunca mostra
   * - showTitle: undefined → mostra apenas se houver mais de 1 card
   */
  const processCardsWithTitles = (cardsArray: DrawerCard[] | undefined): DrawerCard[] | undefined => {
    if (!cardsArray || cardsArray.length === 0) return cardsArray;

    const isSingleCard = cardsArray.length === 1;

    return cardsArray.map(card => {
      // Se showTitle está explicitamente definido, respeita
      if (card.showTitle === true || card.showTitle === false) {
        return {
          ...card,
          title: card.showTitle ? card.title : undefined
        };
      }

      // Default: se só tem 1 card, não mostra título
      if (isSingleCard) {
        return {
          ...card,
          title: undefined
        };
      }

      // Múltiplos cards: mantém título se existir
      return card;
    });
  };

  // === Verificar permissões (FAIL-CLOSED) ===
  // SEGURANÇA: Por padrão, negar acesso. Permissões devem ser explicitamente passadas.
  // Se permissions não for passado ou não tiver canEdit/canDelete, o padrão é FALSE.
  // Isso garante que drawers usados fora do GenericList não exponham botões indevidamente.
  // Ver ADR-004: Fail-Closed Permissions para mais detalhes.
  const canEdit = permissions?.canEdit === true;
  const canDelete = permissions?.canDelete === true;

  // === Agrupar seções por tipo ===
  const formSection = sections.find(s => s.component === 'form');
  const childrenSections = sections.filter(s => s.component === 'children');
  const attachmentsSection = sections.find(s => s.component === 'attachments');

  // === Preparar campos para Bulk Edit ===
  const bulkEditFieldsConfig = useMemo<DrawerBulkEditField[]>(() => {
    if (!isBulkSelecting) return [];

    // Se bulkSelectFields foi passado, usar ele
    if (bulkSelectFields && bulkSelectFields.length > 0) {
      return bulkSelectFields;
    }

    // Caso contrário, extrair dos campos do form
    const allFields: DrawerField[] = formSection?.config?.fields || [];

    // Filtrar apenas campos editáveis (não readonly, não computed)
    return allFields
      .filter(f => !f.readonly && !f.computed && !f.hidden)
      .map(f => ({
        name: f.field,
        label: typeof f.label === 'function' ? f.label(formData) : f.label,
        type: f.type === 'select' || f.type === 'lookup' ? 'select' :
              f.type === 'boolean' || f.type === 'switch' ? 'boolean' :
              'text',
        lookup: f.lookupKey || f.lookup,
        options: f.options,
        placeholder: f.placeholder
      }));
  }, [isBulkSelecting, bulkSelectFields, formSection, formData]);

  // === Tabs disponíveis ===
  // Suporta { id, label, icon?, badge? } para FicharioTabs
  const tabs = useMemo<Array<{ id: string; label: string; icon?: ReactNode; badge?: ReactNode }>>(() => {
    const tabList: Array<{ id: string; label: string; icon?: ReactNode; badge?: ReactNode }> = [{ id: 'dados', label: 'Dados' }];

    childrenSections.forEach(section => {
      if (section.showAsTab) {
        tabList.push({ id: section.id || '', label: section.title || '', icon: section.tabIcon, badge: section.tabBadge });
      }
    });

    if (attachmentsSection?.showAsTab) {
      tabList.push({ id: 'anexos', label: attachmentsSection.title || 'Anexos', icon: attachmentsSection.tabIcon, badge: attachmentsSection.tabBadge });
    }

    customSections.forEach(section => {
      if (section.showAsTab) {
        tabList.push({ id: section.id, label: section.title || '', icon: section.tabIcon, badge: section.tabBadge });
      }
    });

    return tabList;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, customSections]);

  // === Largura responsiva ===
  const drawerWidth = isMobile ? mobileWidth : width;

  // === DESIGN SYSTEM - Padrões visuais do drawer ===
  // Estes estilos definem o padrão visual para todo o sistema
  const styles = {
    // === CORES ===
    colors: {
      primary: 'var(--primary)',        // Azul Joshua - botões principais
      primaryHover: 'var(--primary-700)',   // Hover do azul primário
      success: 'var(--success)',        // Verde - sucesso, quitado
      successBg: 'var(--color-success-bg)',      // Background verde claro
      successBorder: '#bbf7d0',  // Borda verde claro
      danger: 'var(--color-danger)',         // Vermelho - erro, exclusão
      dangerHover: 'var(--danger)',    // Hover vermelho
      dangerDark: 'var(--color-danger-dark)',     // Vermelho escuro
      dangerBg: 'var(--color-danger-bg)',       // Background vermelho claro
      dangerBorder: '#fecaca',   // Borda vermelho claro
      warning: 'var(--color-warning)',        // Amarelo/laranja - alertas
      warningBg: '#fef3c7',      // Background amarelo claro
      warningBorder: '#fde68a',  // Borda amarelo claro
      text: '#1f2937',           // Texto principal
      textSecondary: '#374151',  // Texto secundário
      muted: 'var(--muted)',     // Texto cinza (#6b7280)
      mutedLight: '#9ca3af',     // Cinza mais claro
      border: 'var(--panel-border)', // Bordas padrão
      background: '#fafafa',     // Background de áreas
      backgroundWhite: 'white',  // Background branco
      backgroundHover: 'var(--color-bg-hover)', // Hover de elementos
      link: '#2563eb',           // Links
      linkBg: 'var(--color-primary-light)',         // Background de links
      linkBorder: '#bfdbfe'      // Borda de links
    },

    // === TIPOGRAFIA ===
    typography: {
      // Títulos
      title: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#1f2937'
      },
      subtitle: {
        fontSize: '13px',
        color: 'var(--muted)'
      },
      // Labels
      label: {
        fontSize: '12px',
        color: 'var(--muted)',
        marginBottom: '4px',
        display: 'block'
      },
      labelSmall: {
        fontSize: '11px',
        color: 'var(--muted)'
      },
      labelSection: {
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        marginBottom: '12px'
      },
      // Valores
      value: {
        fontSize: '14px',
        color: '#374151'
      },
      valueBold: {
        fontSize: '14px',
        fontWeight: 700,
        color: '#374151'
      },
      valueLarge: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#374151'
      },
      // Badges/Tags
      badge: {
        fontSize: '12px',
        fontWeight: 500
      },
      // Botões
      button: {
        fontSize: '13px',
        fontWeight: 500
      },
      buttonSmall: {
        fontSize: '12px'
      }
    },

    // === ESPAÇAMENTOS ===
    spacing: {
      // Paddings
      drawerPadding: '20px',
      headerPadding: '16px 20px',
      cardPadding: '12px 16px',
      inputPadding: '10px 12px',
      buttonPadding: '8px 16px',
      buttonPaddingSmall: '4px 10px',
      badgePadding: '4px 10px',
      // Gaps
      gapXs: '4px',
      gapSm: '8px',
      gapMd: '12px',
      gapLg: '16px',
      gapXl: '20px',
      // Margins
      cardMargin: '12px'
    },

    // === BORDAS ===
    borders: {
      radius: '6px',
      radiusSmall: '4px',
      radiusLarge: '8px',
      radiusRound: '12px',
      radiusFull: '50%',
      default: '1px solid var(--panel-border)'
    },

    // === SOMBRAS ===
    shadows: {
      card: '0 1px 3px rgba(0,0,0,0.08)',
      cardHover: '0 2px 6px rgba(0,0,0,0.12)',
      drawer: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      modal: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      button: '0 1px 3px rgba(0,0,0,0.1)',
      buttonHover: '0 2px 6px rgba(0,0,0,0.15)',
      buttonPrimary: '0 2px 6px rgba(21, 70, 128, 0.4)'
    },

    // === TRANSIÇÕES ===
    transitions: {
      default: 'all 0.2s ease',
      fast: 'all 0.15s ease'
    },

    // === CARDS ===
    card: {
      background: 'white',
      border: '1px solid var(--panel-border)',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      padding: '12px 16px',
      marginBottom: '12px'
    },

    // === INPUTS ===
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid var(--panel-border)',
      borderRadius: '6px',
      fontSize: '14px',
      background: 'white',
      outline: 'none'
    },
    inputFocus: {
      borderColor: 'var(--primary)'
    },

    // === BOTÕES ===
    // IMPORTANTE: Ambos os botões têm border de 1px para garantir mesma altura
    buttonPrimary: {
      backgroundColor: 'var(--primary)',
      color: 'white',
      border: '1px solid #154680',
      padding: '8px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '1',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px rgba(21, 70, 128, 0.3)'
    },
    buttonGhost: {
      backgroundColor: 'white',
      color: 'var(--muted)',
      border: '1px solid #e5e7eb',
      padding: '8px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '1',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    buttonIcon: {
      background: 'transparent',
      border: 'none',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '6px',
      transition: 'all 0.15s ease'
    },
    buttonDanger: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      background: 'linear-gradient(135deg, var(--color-danger-gradient) 0%, var(--color-danger-gradient-dark) 100%)',
      color: 'white',
      border: '1px solid var(--color-danger-gradient-dark)',
      borderRadius: '6px',
      fontWeight: 500,
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(139, 0, 0, 0.3)',
      transition: 'all 0.2s ease'
    },

    // === BADGES/STATUS ===
    badge: {
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500
    },
    badgeSuccess: {
      background: 'var(--color-success-bg)',
      color: '#15803d',
      border: '1px solid #bbf7d0'
    },
    badgeWarning: {
      background: '#fef3c7',
      color: '#b45309',
      border: '1px solid #fde68a'
    },
    badgeDanger: {
      background: 'var(--color-danger-bg)',
      color: 'var(--danger)',
      border: '1px solid #fecaca'
    },
    badgeInfo: {
      background: 'var(--color-primary-light)',
      color: '#1d4ed8',
      border: '1px solid #bfdbfe'
    },

    // === MODAIS ===
    modalOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1002
    },
    modalContainer: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '400px',
      width: '90%',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px'
    },
    modalIconContainer: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 600
    },
    modalSubtitle: {
      margin: 0,
      fontSize: '13px',
      color: 'var(--muted)'
    },
    modalFooter: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },

    // === DRAWER ===
    drawerOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000
    },
    drawerContainer: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    drawerHeader: {
      padding: '16px 20px',
      borderBottom: '1px solid var(--panel-border)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'white'
    },
    drawerContent: {
      flex: 1,
      overflow: 'auto',
      padding: '20px',
      background: 'white'
    },

    // === LOADING ===
    loadingSpinner: {
      width: '32px',
      height: '32px',
      border: '3px solid #e5e7eb',
      borderTop: '3px solid #154680',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 16px'
    },

    // === ERROS ===
    errorBanner: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      background: 'var(--color-danger-bg)',
      borderBottom: '1px solid #fecaca',
      color: 'var(--danger)',
      fontSize: '13px'
    }
  };

  // === Não renderizar se fechado (mas permitir animação de fechamento) ===
  if (!isOpen && !isClosing) return null;

  return createPortal(
    <>
      {/* CSS Animations */}
      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(calc(100% + 16px));
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutToRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(calc(100% + 16px));
            opacity: 0;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        .drawer-opening {
          animation: slideInFromRight 0.3s ease-out;
        }

        .drawer-closing {
          animation: slideOutToRight 0.3s ease-in forwards;
        }

        .overlay-opening {
          animation: fadeIn 0.3s ease-out;
        }

        .overlay-closing {
          animation: fadeOut 0.3s ease-in forwards;
        }


        /* Pen Animated — Pen Tilt + Shake (Forte) */
        .pen-animated svg {
          overflow: visible;
        }
        .pen-animated .pen-part {
          transform-origin: 100% 0%;
        }
        .pen-animated .square-part {
          transform-origin: 50% 50%;
        }
        .pen-animated:hover .pen-part {
          animation: penTiltC 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .pen-animated:hover .square-part {
          animation: squareShakeC 0.7s ease;
        }
        @keyframes penTiltC {
          0%   { transform: rotate(0deg) translate(0, 0); }
          10%  { transform: rotate(-18deg) translate(-3px, -3px); }
          20%  { transform: rotate(-7deg) translate(-1px, -1px); }
          30%  { transform: rotate(-15deg) translate(-2.5px, -2.5px); }
          40%  { transform: rotate(-6deg) translate(-1px, -1px); }
          55%  { transform: rotate(-12deg) translate(-2px, -2px); }
          70%  { transform: rotate(-8deg) translate(-1px, -1px); }
          85%  { transform: rotate(-10deg) translate(-1.5px, -1.5px); }
          100% { transform: rotate(-9deg) translate(-1px, -1px); }
        }
        @keyframes squareShakeC {
          0%   { transform: rotate(0deg) translateX(0); }
          10%  { transform: rotate(-5deg) translateX(-2px); }
          20%  { transform: rotate(4deg) translateX(1.5px); }
          30%  { transform: rotate(-3deg) translateX(-1px); }
          40%  { transform: rotate(2.5deg) translateX(1px); }
          55%  { transform: rotate(-1.5deg) translateX(-0.5px); }
          70%  { transform: rotate(1deg) translateX(0.3px); }
          85%  { transform: rotate(-0.5deg); }
          100% { transform: rotate(0deg) translateX(0); }
        }

        /* Check Animated — Double Bounce (CheckCheck) */
        .check-animated svg {
          overflow: visible;
        }
        .check-animated .check-one {
          transform-origin: 50% 50%;
        }
        .check-animated .check-two {
          transform-origin: 50% 50%;
        }
        .check-animated:hover .check-one {
          animation: checkBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .check-animated:hover .check-two {
          animation: checkBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s;
        }
        @keyframes checkBounce {
          0%   { transform: scale(1) rotate(0deg); }
          15%  { transform: scale(1.2) rotate(-8deg); }
          30%  { transform: scale(0.95) rotate(3deg); }
          45%  { transform: scale(1.1) rotate(-5deg); }
          60%  { transform: scale(0.98) rotate(2deg); }
          80%  { transform: scale(1.05) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        /* Calendar Animated — Body Shake + X Bounce (CalendarX2) */
        .cal-animated svg {
          overflow: visible;
        }
        .cal-animated .cal-body {
          transform-origin: 50% 50%;
        }
        .cal-animated .cal-x {
          transform-origin: 50% 50%;
        }
        .cal-animated:hover .cal-body {
          animation: calShake 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .cal-animated:hover .cal-x {
          animation: calXBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.08s;
        }
        @keyframes calShake {
          0%   { transform: rotate(0deg) translateX(0); }
          15%  { transform: rotate(-5deg) translateX(-1.5px); }
          30%  { transform: rotate(4deg) translateX(1px); }
          45%  { transform: rotate(-3deg) translateX(-0.5px); }
          60%  { transform: rotate(2deg) translateX(0.5px); }
          80%  { transform: rotate(-1deg); }
          100% { transform: rotate(0deg) translateX(0); }
        }
        @keyframes calXBounce {
          0%   { transform: scale(1) rotate(0deg); }
          20%  { transform: scale(1.3) rotate(-10deg); }
          40%  { transform: scale(0.9) rotate(5deg); }
          60%  { transform: scale(1.15) rotate(-3deg); }
          80%  { transform: scale(0.98) rotate(1deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        /* Package Animated — Lid Lift + Body Shake (PackageOpen) */
        .pkg-animated svg {
          overflow: visible;
        }
        .pkg-animated .pkg-lid {
          transform-origin: 50% 100%;
        }
        .pkg-animated .pkg-body {
          transform-origin: 50% 100%;
        }
        .pkg-animated:hover .pkg-lid {
          animation: pkgLidLift 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .pkg-animated:hover .pkg-body {
          animation: pkgBodyShake 0.6s ease;
        }
        @keyframes pkgLidLift {
          0%   { transform: translateY(0) rotate(0deg); }
          15%  { transform: translateY(-4px) rotate(-10deg); }
          30%  { transform: translateY(-2px) rotate(-4deg); }
          45%  { transform: translateY(-5px) rotate(-8deg); }
          60%  { transform: translateY(-3px) rotate(-5deg); }
          80%  { transform: translateY(-4px) rotate(-6deg); }
          100% { transform: translateY(-3px) rotate(-5deg); }
        }
        @keyframes pkgBodyShake {
          0%   { transform: rotate(0deg) translateX(0); }
          15%  { transform: rotate(-3deg) translateX(-1px); }
          30%  { transform: rotate(2deg) translateX(0.5px); }
          45%  { transform: rotate(-1.5deg) translateX(-0.5px); }
          60%  { transform: rotate(1deg) translateX(0.3px); }
          80%  { transform: rotate(-0.5deg); }
          100% { transform: rotate(0deg) translateX(0); }
        }
      `}</style>

      {/* Overlay */}
      <div
        className={isClosing ? 'overlay-closing' : (!hasOpenedRef.current ? 'overlay-opening' : '')}
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)', // Safari support
          zIndex: zIndex
        }}
      />

      {/* Drawer */}
      <div
        className={`drawer-container ${isClosing ? 'drawer-closing' : (!hasOpenedRef.current ? 'drawer-opening' : '')}`}
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          bottom: '16px',
          width: `calc(${drawerWidth} - 16px)`, // Subtrair o espaço à direita
          maxWidth: '900px',
          background: 'white',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          borderRadius: '20px',
          zIndex: zIndex + 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header - usa DrawerHeader para padronização */}
        {(() => {
          // Resolver título: se for função, chama com formData; senão usa o valor direto
          const resolvedTitle = typeof title === 'function' ? title(formData) : title;
          const finalTitle = config.titulo || resolvedTitle;

          return renderHeader ? renderHeader({
            mode,
            isEditing,
            isCreating,
            formData,
            config,
            isBulkSelecting,
            // Props adicionais para DrawerHeader
            saving,
            awaitingConnection,
            onClose: handleBackdropClick,
            onCancel: handleCancel,
            onSave: handleSave,
            onEdit: isBulkSelecting ? undefined : handleEdit,
            onDelete: isBulkSelecting ? undefined : handleDeleteClick,
            canEdit: isBulkSelecting || ((loading || externalLoading) && !isCreating) ? false : canEdit,
            canDelete: isBulkSelecting || ((loading || externalLoading) && !isCreating) ? false : canDelete,
            statusBadges: isBulkSelecting ? [] : statusBadges,
            extraButtons: isBulkSelecting ? null : extraButtons,
            onChange: handleFieldChange,
            subtitle,
            title: finalTitle,
          }) : (
            <DrawerHeader
              title={isBulkSelecting ? 'Editar em Lote' : finalTitle}
              subtitle={isBulkSelecting ? `${bulkSelectIds.length} ${bulkSelectIds.length === 1 ? 'item selecionado' : 'itens selecionados'}` : subtitle}
              isEditing={isEditing || isBulkSelecting}
              isCreating={isCreating}
              saving={saving}
              awaitingConnection={awaitingConnection}
              onClose={handleBackdropClick}
              onCancel={handleCancel}
              onSave={handleSave}
              onEdit={isBulkSelecting ? undefined : handleEdit}
              onDelete={isBulkSelecting ? undefined : handleDeleteClick}
              canEdit={isBulkSelecting || ((loading || externalLoading) && !isCreating) ? false : canEdit}
              canDelete={isBulkSelecting || ((loading || externalLoading) && !isCreating) ? false : canDelete}
              statusBadges={isBulkSelecting ? [] : statusBadges}
              formData={formData}
              config={config}
              extraButtons={isBulkSelecting ? null : extraButtons}
              onChange={handleFieldChange}
            />
          );
        })()}

        {awaitingConnection && (
          <p className="reconnecting-save-note" role="status" aria-live="polite">
            O botão volta assim que reconectar. O texto continua aqui.
          </p>
        )}

        {/* Tabs — estilo fichário (Chrome tabs) */}
        <FicharioTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Conteúdo */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          background: 'white'
        }}>
          {(loading || externalLoading) ? (
            <div style={{ padding: '0' }}>
              {/* Skeleton Loading - Card structure */}
              <div style={{
                background: 'white',
                border: '1px solid var(--panel-border)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
                {/* Skeleton rows */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-pulse" style={{ height: '12px', width: '60px', borderRadius: '4px', marginBottom: '8px' }} />
                    <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-pulse" style={{ height: '12px', width: '80px', borderRadius: '4px', marginBottom: '8px' }} />
                    <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-pulse" style={{ height: '12px', width: '50px', borderRadius: '4px', marginBottom: '8px' }} />
                    <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-pulse" style={{ height: '12px', width: '70px', borderRadius: '4px', marginBottom: '8px' }} />
                    <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                  </div>
                </div>
                <div>
                  <div className="skeleton-pulse" style={{ height: '12px', width: '90px', borderRadius: '4px', marginBottom: '8px' }} />
                  <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                </div>
              </div>

              {/* Second skeleton card */}
              <div style={{
                background: 'white',
                border: '1px solid var(--panel-border)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-pulse" style={{ height: '12px', width: '55px', borderRadius: '4px', marginBottom: '8px' }} />
                    <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-pulse" style={{ height: '12px', width: '65px', borderRadius: '4px', marginBottom: '8px' }} />
                    <div className="skeleton-pulse" style={{ height: '40px', borderRadius: '6px' }} />
                  </div>
                </div>
              </div>

              {/* Skeleton CSS animation */}
              <style>{`
                .skeleton-pulse {
                  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                  background-size: 200% 100%;
                  animation: skeleton-shimmer 1.5s infinite;
                }
                @keyframes skeleton-shimmer {
                  0% { background-position: 200% 0; }
                  100% { background-position: -200% 0; }
                }
              `}</style>
            </div>
          ) : isBulkSelecting ? (
            // === Modo Bulk Edit: renderizar BulkEditForm ===
            <BulkEditForm
              fields={bulkEditFieldsConfig}
              values={formData}
              onChange={handleFieldChange}
              selectedFields={selectedBulkFields}
              onToggleField={toggleBulkField}
              lookups={lookups}
              asyncSearchFunctions={asyncSearchFunctions}
            />
          ) : renderContent ? (
            // Renderização customizada (inclui isFieldRequired para Schema-driven UI e appliedFilters)
            renderContent({ mode, isEditing, isCreating, formData, onChange: handleFieldChange, lookups, errors, config, isFieldRequired, tableName, appliedFilters, processedDefaultValues })
          ) : (
            <>
              {/* Tab Dados */}
              {activeTab === 'dados' && (
                <>
                  {/* Custom Sections before cards */}
                  {customSections
                    .filter(s => !s.showAsTab && s.position === 'before-cards')
                    .map(section => {
                      // Usar componente cacheado para evitar remount quando drawer pai recria a função
                      const CustomComponent = getCachedComponent(section.id, section.component);
                      return (
                        <CustomComponent
                          key={section.id}
                          record={formData}
                          mode={isEditing ? 'edit' : 'view'}
                          onChange={handleFieldChange}
                          lookups={lookups}
                          config={config}
                          isCreating={isCreating}
                          {...section.props}
                        />
                      );
                    })}

                  {/* FormFieldsSection com cards */}
                  {formSection && (
                    <FormFieldsSection
                      fields={formSection.config?.fields}
                      values={formData}
                      onChange={handleFieldChange}
                      mode={isEditing ? 'edit' : 'view'}
                      lookups={lookups}
                      errors={errors}
                      config={config}
                      cards={processCardsWithTitles(cards.length > 0 ? cards : formSection.config?.cards)}
                    />
                  )}

                  {/* Custom Sections after form section (antes de sideBySideCards) */}
                  {customSections
                    .filter(s => !s.showAsTab && (s.position === 'after-form-section' || s.position?.startsWith('after-card-')))
                    .map(section => {
                      const CustomComponent = getCachedComponent(section.id, section.component);
                      return (
                        <CustomComponent
                          key={section.id}
                          record={formData}
                          mode={isEditing ? 'edit' : 'view'}
                          onChange={handleFieldChange}
                          lookups={lookups}
                          config={config}
                          {...section.props}
                        />
                      );
                    })}

                  {/* Cards lado a lado (Composição | Situação) */}
                  {sideBySideCards && (
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      {sideBySideCards.map(card => {
                        // Conteúdo do card (campos inline)
                        const cardContent = (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {card.fields.map(field => {
                              const fieldConfig = formSection?.config?.fields?.find(f => f.field === field.field) || field;
                              const value = formData[field.field];

                              // Renderizar campo inline (label | valor)
                              return (
                                <div key={field.field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span style={{
                                    fontSize: '11px',
                                    color: field.labelColor || 'var(--muted)'
                                  }}>
                                    {typeof field.label === 'function' ? field.label(formData) : field.label}
                                  </span>
                                  <span style={{
                                    fontSize: '14px',
                                    fontWeight: field.bold ? 700 : 400,
                                    fontStyle: field.italic ? 'italic' : 'normal',
                                    color: field.valueColor || '#374151'
                                  }}>
                                    {typeof field.format === 'function'
                                      ? field.format(value, formData, config)
                                      : formatValue(fieldConfig, value)
                                    }
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );

                        // Se card tem título, usar FieldsetCard
                        if (card.title) {
                          return (
                            <FieldsetCard
                              key={card.id}
                              title={card.title}
                              style={{ flex: '1 1 0', marginBottom: 0 }}
                            >
                              {cardContent}
                            </FieldsetCard>
                          );
                        }

                        // Sem título: usar div padrão
                        return (
                          <div key={card.id} style={{ ...styles.card, flex: '1 1 0', marginBottom: 0 }}>
                            {cardContent}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Cards Expansíveis (Movimentos, Anexos, etc.) */}
                  {expandableCards
                    .filter(card => !card.showAsTab)
                    .map(card => (
                      <ExpandableCard
                        key={card.id}
                        title={card.title}
                        icon={card.icon}
                        badge={typeof card.badge === 'function' ? card.badge(formData) : card.badge}
                        badgeColor={card.badgeColor}
                        expanded={expandedCards[card.id]}
                        onToggle={() => toggleExpandedCard(card.id)}
                        headerAction={card.headerAction && (
                          <div onClick={(e) => e.stopPropagation()}>
                            {typeof card.headerAction === 'function'
                              ? card.headerAction({ mode, isEditing, formData, config })
                              : card.headerAction
                            }
                          </div>
                        )}
                        noPadding={card.noPadding}
                      >
                        {card.component && (
                          <card.component
                            record={formData}
                            mode={isEditing ? 'edit' : 'view'}
                            onChange={handleFieldChange}
                            lookups={lookups}
                            config={config}
                            {...card.props}
                          />
                        )}
                        {card.render && card.render({ mode, isEditing, formData, onChange: handleFieldChange, lookups, config })}
                      </ExpandableCard>
                    ))}

                  {/* Custom Sections (inline, no tab Dados) */}
                  {customSections
                    .filter(s => !s.showAsTab && s.position === 'after-form')
                    .map(section => {
                      const CustomComponent = getCachedComponent(section.id, section.component);
                      return (
                        <CustomComponent
                          key={section.id}
                          record={formData}
                          mode={isEditing ? 'edit' : 'view'}
                          onChange={handleFieldChange}
                          lookups={lookups}
                          config={config}
                          {...section.props}
                        />
                      );
                    })}
                </>
              )}

              {/* Tabs de Children */}
              {childrenSections
                .filter(s => s.showAsTab)
                .map(section => (
                  activeTab === section.id && (
                    <ChildrenSection
                      key={section.id}
                      config={section.config}
                      parentId={recordId}
                      mode={isEditing ? 'edit' : 'view'}
                      lookups={lookups}
                      permissions={section.config?.permissions}
                      {...otherProps}
                    />
                  )
                ))}

              {/* Tab Anexos */}
              {activeTab === 'anexos' && attachmentsSection && (
                <AttachmentsSection
                  config={attachmentsSection.config}
                  entityId={recordId}
                  mode={isEditing ? 'edit' : 'view'}
                  permissions={attachmentsSection.config?.permissions}
                />
              )}

              {/* Custom Sections (tabs) */}
              {customSections
                .filter(s => s.showAsTab && activeTab === s.id)
                .map(section => {
                  const CustomComponent = getCachedComponent(section.id, section.component);
                  return (
                    <CustomComponent
                      key={section.id}
                      record={formData}
                      mode={isEditing ? 'edit' : 'view'}
                      onChange={handleFieldChange}
                      lookups={lookups}
                      config={config}
                      {...section.props}
                    />
                  );
                })}
            </>
          )}
        </div>

        {/* Footer customizado ou custom sections */}
        {renderFooter ? (
          <div style={{ borderTop: '1px solid var(--panel-border)', padding: '16px 20px' }}>
            {renderFooter({ mode, isEditing, isCreating, formData, config })}
          </div>
        ) : (
          customSections
            .filter(s => s.position === 'footer')
            .map(section => {
              const CustomComponent = section.component;
              return (
                <div key={section.id} style={{ borderTop: '1px solid var(--panel-border)', padding: '16px 20px' }}>
                  <CustomComponent
                    record={formData}
                    mode={isEditing ? 'edit' : 'view'}
                    onChange={handleFieldChange}
                    lookups={lookups}
                    config={config}
                    {...section.props}
                  />
                </div>
              );
            })
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndex + 2
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--color-danger-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrashAnimatedIcon size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Excluir registro?</h3>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)' }}>
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-add undo-animated"
              >
                <UndoAnimatedIcon size={15} /> <span className="btn-text">Cancelar</span>
              </button>
              <button
                onClick={handleDelete}
                className="btn-add gf-trash-animated"
              >
                <TrashAnimatedIcon size={14} />
                <span className="btn-text">Excluir</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Descartar Alterações */}
      {showDiscardModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndex + 2
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600 }}>
              Descartar alterações?
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: 'var(--muted)' }}>
              Você tem alterações não salvas. Deseja descartá-las?
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDiscardModal(false)}
                className="btn-add undo-animated"
              >
                <UndoAnimatedIcon size={15} /> <span className="btn-text">Continuar editando</span>
              </button>
              <button
                onClick={handleConfirmDiscard}
                className="btn-add gf-trash-animated"
              >
                <TrashAnimatedIcon size={14} />
                <span className="btn-text">Descartar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

// Helper para formatação de valores
function formatValue(field: DrawerField | undefined, value: unknown): ReactNode {
  if (value === null || value === undefined || value === '') return '-';

  switch (field?.type) {
    case 'currency':
    case 'money':
      // Usa formatMoney padronizado (sem R$, com separador de milhar e 2 casas decimais)
      return formatMoney(value as number);

    case 'date':
      return formatDate(value as string);

    case 'percent':
      return `${parseFloat(String(value)).toFixed(2)}%`;

    default:
      return String(value);
  }
}

/**
 * Exemplo de uso completo (padrão LancamentoDrawer):
 *
 * const fields = [
 *   { field: 'parte_id', label: 'Cliente', type: 'select', lookupKey: 'partes', searchable: true, required: true },
 *   { field: 'data_vencimento', label: 'Vencimento', type: 'date', required: true },
 *   { field: 'descricao', label: 'Descrição', type: 'text', required: true },
 *   { field: 'valor_original', label: 'Valor (R$)', type: 'currency', required: true },
 * ];
 *
 * const cards = [
 *   {
 *     id: 'info',
 *     rows: [
 *       { columns: '1fr 140px', fields: ['parte_id', 'data_vencimento'] },
 *       { columns: '1fr 140px', fields: ['descricao', 'valor_original'] },
 *     ]
 *   }
 * ];
 *
 * const sideBySideCards = [
 *   {
 *     id: 'composicao',
 *     fields: [
 *       { field: 'valor_original', label: 'Valor Original (R$)', type: 'currency' },
 *       { field: 'desconto', label: 'Desconto (R$)', type: 'currency' },
 *     ]
 *   },
 *   {
 *     id: 'situacao',
 *     fields: [
 *       { field: 'valor_final', label: 'Valor Final (R$)', type: 'currency' },
 *       { field: 'valor_saldo', label: 'Saldo (R$)', type: 'currency', bold: true, italic: true },
 *     ]
 *   }
 * ];
 *
 * const expandableCards = [
 *   {
 *     id: 'movimentos',
 *     title: 'Movimentos',
 *     icon: <DollarSign size={16} />,
 *     badge: (data) => data.movimentos?.length || 0,
 *     defaultExpanded: true,
 *     component: MovimentosSection,
 *     headerAction: ({ isEditing }) => isEditing && <button>+ Adicionar</button>
 *   }
 * ];
 *
 * const statusBadges = [
 *   {
 *     condition: (data) => data.quitado,
 *     label: 'Quitado',
 *     bgColor: '#dcfce7',
 *     textColor: 'var(--success-700)'
 *   }
 * ];
 *
 * <GenericDrawer
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   mode={mode}
 *   recordId={selectedId}
 *   sections={[{ component: 'form', config: { fields } }]}
 *   cards={cards}
 *   sideBySideCards={sideBySideCards}
 *   expandableCards={expandableCards}
 *   statusBadges={statusBadges}
 *   onLoadRecord={async (id) => { ... }}
 *   onSaveRecord={async (data, isCreating) => { ... }}
 *   onDeleteRecord={async (id) => { ... }}
 *   lookups={{ partes, contas }}
 *   config={{ labelParte: 'Cliente' }}
 * />
 */
